/**
 * SEO Editor v3 -- the typed section contract.
 *
 * PAWTENANT-SEO-EDITOR-V3-NOCODE-WORKSPACE-001
 *
 * A page that is "fully section-editable" describes itself with ONE of these
 * maps, kept next to its component (src/pages/<page>/content.ts). The map is
 * the single source of truth for three consumers that must never disagree:
 *
 *   1. the page component, which renders every value through
 *      useSeoPageContent() and therefore cannot show a string the map does not
 *      know about;
 *   2. the editor, which lists exactly these sections and fields, in this
 *      order, with these labels and limits;
 *   3. the server validator, which refuses an override for a field id that is
 *      not in the map, is not editable, or breaks its constraints.
 *
 * WHY IDS ARE STRINGS LIKE "hero.heading" AND NOT COMPONENT NAMES
 * A field id is a stable address that survives a redesign. The component that
 * renders "hero.heading" may change its markup, classes or position; the
 * published override keyed by that id keeps applying. A CSS selector or a
 * file path would break on the first refactor -- and would hand an editor a
 * way to address things nobody meant to be editable.
 *
 * WHAT IS DELIBERATELY NOT HERE
 * Prices, attribution-wrapped links, legal disclaimers and shared design
 * components (pricing cards, planners, trust strips) appear as
 * `managed_design` entries so the outline is complete and honest, but they
 * carry no editable value.
 */

export type SeoFieldKind =
  | "eyebrow"
  | "heading"
  | "paragraph"
  | "list_item"
  | "cta_label"
  | "cta_url"
  | "faq_question"
  | "faq_answer"
  | "image"
  | "image_alt"
  | "badge"
  | "variant"
  | "toggle";

export type SeoFieldSource = "registry" | "published_override" | "source_fallback" | "managed_design";

export interface SeoFieldConstraints {
  minLength?: number;
  maxLength?: number;
  /** For cta_url: the only destinations this button may point at. */
  allowedRoutes?: string[];
  /** For variant / toggle: the only values accepted. */
  allowedValues?: string[];
  /** Advisory length band shown to the editor (not enforced). */
  recommended?: [number, number];
}

export interface SeoEditableField {
  /** Stable id, e.g. "hero.heading". Unique within the page. */
  id: string;
  label: string;
  kind: SeoFieldKind;
  /** The compiled default -- what the page shows with no override. */
  value: string;
  editable: boolean;
  source: SeoFieldSource;
  /** Plain-language answer to "where does this appear?". */
  where?: string;
  /** Plain-language answer to "what does changing this do?". */
  help?: string;
  constraints?: SeoFieldConstraints;
}

/** One column of a collection item, e.g. the "question" of an FAQ. */
export interface SeoCollectionColumn {
  key: string;
  label: string;
  kind: SeoFieldKind;
  constraints?: SeoFieldConstraints;
  /** A managed column is carried through untouched and never shown as editable. */
  managed?: boolean;
}

export type SeoCollectionKind = "faq" | "cards" | "bullets" | "steps";

export interface SeoEditableCollection {
  /** Stable id, e.g. "faq.items". */
  id: string;
  label: string;
  kind: SeoCollectionKind;
  columns: SeoCollectionColumn[];
  /** Compiled defaults. Keyed items (cards) carry a `key` column. */
  items: Array<Record<string, string>>;
  /** Items may be re-ordered by the editor. */
  reorderable: boolean;
  /** Items may be added (never true for keyed card sets). */
  addable: boolean;
  /** Items may be removed (never true for keyed card sets). */
  removable: boolean;
  minItems: number;
  maxItems: number;
  /** True when items are identified by a `key` column (cards); false for FAQs / bullets. */
  keyed: boolean;
  where?: string;
  help?: string;
}

export type SeoSectionType = "hero" | "content" | "cards" | "comparison" | "faq" | "cta" | "custom";

export interface SeoEditableSection {
  /** Stable id, e.g. "hero". */
  id: string;
  label: string;
  /** 1-based render order. */
  order: number;
  sectionType: SeoSectionType;
  /** Plain-language description shown in the outline. */
  description?: string;
  fields: SeoEditableField[];
  collections: SeoEditableCollection[];
  /** A managed section is a shared design component; nothing in it is editable. */
  managed: boolean;
}

export interface SeoPageContentMap {
  routePath: string;
  /** Bumped when the shape of the map changes in a way an old override cannot satisfy. */
  version: number;
  sections: SeoEditableSection[];
}

/** An image override as stored in a published payload. */
export interface SeoImageOverride {
  asset_id: string;
  mime_type: string;
  width?: number;
  height?: number;
}

/** The value an override may hold, by field kind. */
export type SeoOverrideValue = string | SeoImageOverride | Array<Record<string, string>>;

/** payload.content_overrides */
export type SeoContentOverrides = Record<string, SeoOverrideValue>;

export const CONTENT_OVERRIDES_KEY = "content_overrides";

/** Section visibility is a toggle field named "<section>.visibility". */
export const VISIBILITY_FIELD_SUFFIX = ".visibility";
export const VISIBILITY_VALUES = ["visible", "hidden"] as const;

export function fieldById(map: SeoPageContentMap, fieldId: string): SeoEditableField | null {
  for (const s of map.sections) for (const f of s.fields) if (f.id === fieldId) return f;
  return null;
}

export function collectionById(map: SeoPageContentMap, collectionId: string): SeoEditableCollection | null {
  for (const s of map.sections) for (const c of s.collections) if (c.id === collectionId) return c;
  return null;
}

export function sectionOfField(map: SeoPageContentMap, id: string): SeoEditableSection | null {
  for (const s of map.sections) {
    if (s.fields.some((f) => f.id === id) || s.collections.some((c) => c.id === id)) return s;
  }
  return null;
}
